// Centro de Impresión QR Masivo — /bodega/qr-imprimir
// Filtra htas por pañol/familia/origen/calidad y genera PDF masivo de etiquetas QR.
import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Loader2, Printer, Filter, FileDown, Package, AlertCircle, Eye } from 'lucide-react';
import { toast } from 'sonner';

const DEFAULT_FILTERS = {
  panol_id: '',
  familia: '',
  origen: '',
  calidad: '',
  solo_inventariables: false,
  solo_sin_imprimir: false,
};

export default function QRImprimirPage() {
  const [panoles, setPanoles] = useState([]);
  const [familias, setFamilias] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Carga inicial de pañoles y familias
  useEffect(() => {
    (async () => {
      try {
        const [pRes, fRes] = await Promise.all([
          api.get('/bodega/panoles?activos_solo=true'),
          api.get('/bodega/familias-herramientas'),
        ]);
        setPanoles(pRes.data.items || []);
        setFamilias(fRes.data.items || []);
      } catch (err) {
        toast.error('Error cargando pañoles o familias');
      }
    })();
  }, []);

  const loadPreview = useCallback(async () => {
    setLoadingPreview(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== '' && v !== false && v !== null && v !== undefined) params.set(k, v);
      });
      const res = await api.get(`/bodega/herramientas/qr-imprimir/preview?${params.toString()}`);
      setPreview(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error en preview');
    } finally { setLoadingPreview(false); }
  }, [filters]);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => setFilters(DEFAULT_FILTERS);

  const imprimirPDF = async () => {
    if (!preview || preview.total === 0) {
      toast.error('No hay herramientas para imprimir con estos filtros');
      return;
    }
    if (preview.total > 2000) {
      toast.error('Máximo 2000 etiquetas. Afina los filtros.');
      return;
    }
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== '' && v !== false && v !== null && v !== undefined) params.set(k, v);
      });
      params.set('base_url', window.location.origin);

      const res = await api.get(`/bodega/herramientas/qr-imprimir/pdf?${params.toString()}`, {
        responseType: 'blob',
      });

      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ctx = filters.panol_id
        ? (panoles.find((p) => p.id === filters.panol_id)?.nombre || 'panol').replace(/\s/g, '_')
        : 'todas';
      a.download = `qr_${ctx}_${preview.total}_${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`PDF generado · ${preview.total} etiquetas · ${preview.paginas_a4_estimadas} hojas`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error generando PDF');
    } finally { setDownloading(false); }
  };

  const activeFiltersCount = Object.values(filters).filter((v) => v !== '' && v !== false).length;

  return (
    <div className="p-6 space-y-6" data-testid="qr-imprimir-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Printer className="w-8 h-8 text-indigo-600" />
            Centro de Impresión QR
          </h1>
          <p className="text-slate-500 mt-1">
            Genera etiquetas QR masivas para herramientas por pañol, familia u origen.
          </p>
        </div>
        {activeFiltersCount > 0 && (
          <Button variant="outline" onClick={resetFilters} data-testid="btn-reset-filters">
            Limpiar filtros ({activeFiltersCount})
          </Button>
        )}
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <Filter className="w-4 h-4" /> Filtros
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs text-slate-500">Pañol</Label>
              <Select
                value={filters.panol_id || '__all__'}
                onValueChange={(v) => updateFilter('panol_id', v === '__all__' ? '' : v)}
              >
                <SelectTrigger data-testid="select-panol"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos los pañoles</SelectItem>
                  <SelectItem value="sin_panol">Sin pañol asignado</SelectItem>
                  {panoles.map((p, idx) => (
                    <SelectItem key={`${p.id}-${idx}`} value={p.id}>
                      {p.nombre} ({p.total_herramientas})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Familia</Label>
              <Select
                value={filters.familia || '__all__'}
                onValueChange={(v) => updateFilter('familia', v === '__all__' ? '' : v)}
              >
                <SelectTrigger data-testid="select-familia"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todas las familias</SelectItem>
                  {familias.map((f) => (
                    <SelectItem key={f.nombre || f.id} value={f.nombre}>{f.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Origen</Label>
              <Select
                value={filters.origen || '__all__'}
                onValueChange={(v) => updateFilter('origen', v === '__all__' ? '' : v)}
              >
                <SelectTrigger data-testid="select-origen"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos los orígenes</SelectItem>
                  <SelectItem value="access_import">Importadas de Access</SelectItem>
                  <SelectItem value="nativa">Nativas (nube)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Calidad del dato</Label>
              <Select
                value={filters.calidad || '__all__'}
                onValueChange={(v) => updateFilter('calidad', v === '__all__' ? '' : v)}
              >
                <SelectTrigger data-testid="select-calidad"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todas</SelectItem>
                  <SelectItem value="desc_incompleta">Descripción con ***</SelectItem>
                  <SelectItem value="sin_familia">Sin familia</SelectItem>
                  <SelectItem value="sin_precio">Sin precio</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-2 pt-2">
            <Checkbox
              id="chk-inv"
              checked={filters.solo_inventariables}
              onCheckedChange={(v) => updateFilter('solo_inventariables', !!v)}
              data-testid="chk-solo-inventariables"
            />
            <label htmlFor="chk-inv" className="text-sm text-slate-700 cursor-pointer">
              Solo herramientas inventariables (con <code className="text-xs bg-slate-100 px-1 rounded">***</code> en descripción)
            </label>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="chk-sin-imp"
              checked={filters.solo_sin_imprimir}
              onCheckedChange={(v) => updateFilter('solo_sin_imprimir', !!v)}
              data-testid="chk-solo-sin-imprimir"
            />
            <label htmlFor="chk-sin-imp" className="text-sm text-slate-700 cursor-pointer">
              Solo las <b>nunca impresas</b> antes (evita reimprimir etiquetas ya pegadas en terreno)
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Preview + botón */}
      <Card>
        <CardContent className="p-6">
          {loadingPreview ? (
            <div className="flex items-center gap-2 text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Calculando lote...
            </div>
          ) : !preview ? null : (
            <div className="space-y-4">
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                  <div className="text-5xl font-bold text-indigo-700" data-testid="total-etiquetas">
                    {preview.total.toLocaleString('es-CL')}
                  </div>
                  <div className="text-sm text-slate-500 mt-1">
                    etiquetas · <b>{preview.paginas_a4_estimadas}</b> hojas A4 · 24 etiquetas/hoja
                  </div>
                  {preview.panol_nombre && (
                    <Badge className="mt-2 bg-emerald-100 text-emerald-800 border-emerald-200">
                      Pañol: {preview.panol_nombre}
                    </Badge>
                  )}
                </div>
                <Button
                  size="lg"
                  disabled={preview.total === 0 || downloading || preview.total > 2000}
                  onClick={imprimirPDF}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="btn-imprimir-pdf"
                >
                  {downloading ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Generando PDF...</>
                  ) : (
                    <><FileDown className="w-5 h-5 mr-2" /> Imprimir QR ({preview.total})</>
                  )}
                </Button>
              </div>

              {preview.total === 0 && (
                <div className="flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3 text-sm" data-testid="empty-state">
                  <AlertCircle className="w-4 h-4" />
                  No hay herramientas que coincidan con estos filtros.
                </div>
              )}

              {preview.truncated && (
                <div className="flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3 text-sm" data-testid="truncated-warning">
                  <AlertCircle className="w-4 h-4" />
                  <span>
                    Hay <b>{preview.total_real?.toLocaleString('es-CL')}</b> herramientas que cumplen los filtros, pero sólo se imprimirán las primeras <b>{preview.total.toLocaleString('es-CL')}</b>. Afina los filtros para cubrir el resto.
                  </span>
                </div>
              )}

              {preview.total > 2000 && (
                <div className="flex items-center gap-2 text-red-700 bg-red-50 border border-red-200 rounded-md p-3 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  Supera el límite de 2000. Afina los filtros (por ejemplo, por pañol o familia).
                </div>
              )}

              {/* Distribución por familia */}
              {preview.por_familia && preview.por_familia.length > 0 && (
                <div>
                  <div className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <Package className="w-4 h-4" /> Distribución por familia
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {preview.por_familia.slice(0, 12).map((f) => (
                      <Badge key={f.familia} variant="outline" className="text-xs">
                        {f.familia} · {f.count}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Sample */}
              {preview.sample && preview.sample.length > 0 && (
                <div>
                  <div className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <Eye className="w-4 h-4" /> Muestra (primeras {preview.sample.length})
                  </div>
                  <div className="border rounded-md overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-slate-600">
                        <tr>
                          <th className="text-left px-3 py-2">Código</th>
                          <th className="text-left px-3 py-2">Descripción</th>
                          <th className="text-left px-3 py-2">Familia</th>
                          <th className="text-left px-3 py-2">Origen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.sample.map((m) => (
                          <tr key={m.codigo} className="border-t">
                            <td className="px-3 py-1.5 font-mono text-xs text-indigo-700">{m.codigo}</td>
                            <td className="px-3 py-1.5 text-slate-700">{m.descripcion}</td>
                            <td className="px-3 py-1.5 text-slate-500">{m.familia || '—'}</td>
                            <td className="px-3 py-1.5">
                              <Badge variant="outline" className="text-[10px]">
                                {m.origen === 'access_import' ? 'Access' : 'Nativa'}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
